// src/pages/admin/AdminOrderUnit.jsx
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  getOrderCharges,
  updateOrderCharges,
  DEFAULT_ORDER_CHARGES,
} from "../../services/firestoreService";
import Spinner from "../../components/common/Spinner";
import toast from "react-hot-toast";
import { FiTruck, FiPercent, FiSave, FiInfo } from "react-icons/fi";

export default function AdminOrderUnit() {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: DEFAULT_ORDER_CHARGES });

  useEffect(() => {
    getOrderCharges()
      .then((data) => {
        reset({
          deliveryPerKg: data.deliveryPerKg,
          sgstPercent:   data.sgstPercent,
          cgstPercent:   data.cgstPercent,
        });
      })
      .catch((err) => console.error("Failed to load order charges:", err))
      .finally(() => setLoading(false));
  }, [reset]);

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      await updateOrderCharges({
        deliveryPerKg: Number(data.deliveryPerKg) || 0,
        sgstPercent:   Number(data.sgstPercent) || 0,
        cgstPercent:   Number(data.cgstPercent) || 0,
      });
      toast.success("Order charges updated");
    } catch (err) {
      console.error("Failed to save order charges:", err);
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Reusable numeric input with an icon on the left and a unit label on the right
  const Field = ({ label, name, icon: Icon, suffix }) => (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
        {label}
      </label>
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
        )}
        <input
          {...register(name, {
            required: "Required",
            min: { value: 0, message: "Must be 0 or more" },
          })}
          type="number"
          step="0.01"
          min="0"
          className={`w-full rounded-xl border bg-gray-50 text-sm text-gray-900 px-3 py-2.5 pl-9 ${
            suffix ? "pr-12" : ""
          } transition-all outline-none
            focus:bg-white focus:border-primary-400 focus:ring-2 focus:ring-primary-100 hover:border-gray-300
            ${errors[name] ? "border-red-300 bg-red-50" : "border-gray-200"}`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">
            {suffix}
          </span>
        )}
      </div>
      {errors[name] && <p className="text-xs text-red-500 mt-1">⚠ {errors[name].message}</p>}
    </div>
  );

  if (loading) return <Spinner center />;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Order Unit</h1>
        <p className="text-sm text-gray-400 mt-0.5">
          Set the per-kg delivery charge and tax rates applied to every online order at checkout.
        </p>
      </div>

      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm max-w-xl overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-50">
          <div className="w-7 h-7 rounded-lg bg-primary-600 flex items-center justify-center">
            <FiTruck className="w-3.5 h-3.5 text-white" />
          </div>
          <h2 className="font-semibold text-gray-900 text-sm">Delivery &amp; Tax Settings</h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-5">
          <Field label="Delivery Charge (per kg)" name="deliveryPerKg" icon={FiTruck} suffix="₹/kg" />

          <div className="grid grid-cols-2 gap-4">
            <Field label="SGST" name="sgstPercent" icon={FiPercent} suffix="%" />
            <Field label="CGST" name="cgstPercent" icon={FiPercent} suffix="%" />
          </div>

          <div className="flex items-start gap-2 bg-primary-50 border border-primary-100 rounded-xl px-3.5 py-3">
            <FiInfo className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-primary-700 leading-relaxed">
              Delivery charge = total order weight (kg) × the rate above. SGST and CGST are each
              applied on the order subtotal. These values apply to every new order placed from the
              website — existing orders keep the rates that were active when they were placed.
            </p>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <FiSave className="w-4 h-4" /> {saving ? "Saving..." : "Save Changes"}
          </button>
        </form>
      </div>
    </div>
  );
}